import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ContentPasteOffOutlinedIcon from "@mui/icons-material/ContentPasteOffOutlined";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import GridOnIcon from "@mui/icons-material/GridOn";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import PhotoOutlinedIcon from "@mui/icons-material/PhotoOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import { Tooltip } from "@mui/material";
import { Box } from "@mui/system";
import type React from "react";

/**
 * Types of icons displayed according to incoming document type
 */
const iconTypes: Record<string, React.ReactNode> = {
	pdf: (
		<PictureAsPdfOutlinedIcon
			data-testid="pdfIcon"
			sx={(theme) => ({ color: theme?.vars?.palette.additionalColors.red.main })}
		/>
	),
	csv: (
		<GridOnIcon data-testid="csvIcon" sx={(theme) => ({ color: theme?.vars?.palette.additionalColors.green.main })} />
	),
	doc: (
		<ArticleOutlinedIcon
			data-testid="docIcon"
			sx={(theme) => ({ color: theme?.vars?.palette.primaryColors.blue.main })}
		/>
	),
	docx: (
		<ArticleOutlinedIcon
			data-testid="docxIcon"
			sx={(theme) => ({ color: theme?.vars?.palette.primaryColors.blue.main })}
		/>
	),
	dotx: (
		<ArticleOutlinedIcon
			data-testid="docxIcon"
			sx={(theme) => ({ color: theme?.vars?.palette.primaryColors.blue.main })}
		/>
	),
	cel: (
		<GridOnIcon data-testid="celIcon" sx={(theme) => ({ color: theme?.vars?.palette.additionalColors.green.main })} />
	),
	xls: (
		<GridOnIcon data-testid="xlsIcon" sx={(theme) => ({ color: theme?.vars?.palette.additionalColors.green.main })} />
	),
	xlsx: (
		<GridOnIcon data-testid="xlsxIcon" sx={(theme) => ({ color: theme?.vars?.palette.additionalColors.green.main })} />
	),
	txt: (
		<ArticleOutlinedIcon
			data-testid="txtIcon"
			sx={(theme) => ({ color: theme?.vars?.palette.primaryColors.blue.main })}
		/>
	),
	sec: (
		<KeyOutlinedIcon
			data-testid="secIcon"
			sx={(theme) => ({ color: theme?.vars?.palette.additionalColors.yellow.main })}
		/>
	),
	tif: (
		<PhotoOutlinedIcon
			data-testid="tifIcon"
			sx={(theme) => ({ color: theme?.vars?.palette.primaryColors.azure.main })}
		/>
	),
	zip: <FolderZipIcon data-testid="ZIPIcon" color="disabled" />,
	default: <QuizOutlinedIcon data-testid="defaultIcon" color="disabled" />,
};

const getDocumentTooltip = (format: string) => `dokument ve formátu ${format}`;

/**
 * Types of strings displayed according to incoming document type
 */
const tooltipTypes: Record<string, string> = {
	pdf: getDocumentTooltip("pdf"),
	csv: getDocumentTooltip("csv"),
	doc: getDocumentTooltip("doc"),
	docx: getDocumentTooltip("docx"),
	dotx: getDocumentTooltip("dotx"),
	cel: getDocumentTooltip("cel"),
	xls: getDocumentTooltip("xls"),
	xlsx: getDocumentTooltip("xlsx"),
	txt: getDocumentTooltip("txt"),
	sec: getDocumentTooltip("sec"),
	tif: getDocumentTooltip("tif"),
	zip: getDocumentTooltip("zip"),
	default: "neznámý soubor",
};

/**
 * Dynamic icon of document type based on document suffix
 */
const DocumentTypeIcon: React.FC<{ documentType: string | undefined | null }> = ({ documentType }) => {
	const loweredDocumentType = documentType?.toLowerCase() ?? documentType;

	const tooltipText = loweredDocumentType
		? tooltipTypes[loweredDocumentType as keyof typeof tooltipTypes]
		: "neznámý typ";

	const icon = loweredDocumentType ? (
		iconTypes[loweredDocumentType as keyof typeof iconTypes]
	) : (
		<ContentPasteOffOutlinedIcon color="disabled" />
	);

	return (
		<Tooltip title={tooltipText}>
			<Box>{icon}</Box>
		</Tooltip>
	);
};

export default DocumentTypeIcon;
